import { formatMonthDay, formatMonthDayTime } from '../components/PartnerCandidateItem'
import type { ProgressReportDay, ReportStatusCode, TaskProgressSummary } from '../api/taskProgressApi'
import type { ConfirmProgressStep } from './partnerStatus'

// 시공 진행 요약 ④작업요청 ⑤작업보고 단계(B-08) — 일정확인 3단계(getConfirmProgressSteps) 뒤에 이어 붙인다.
// 데이터는 GET /schedule/tasks/{taskId}/progress-summary. 값이 없으면 채우지 않는다('-').

export const WORK_ORDER_DETAIL_KEY = 'work-order'
export const WORK_REPORT_DETAIL_KEY = 'work-report'

/** 현장보고 상태 이름(공통코드 ZEU_FIELD_REPORT_STATUS) */
export const REPORT_STATUS_LABEL: Record<ReportStatusCode, string> = {
  IN_PROGRESS: '작업중',
  COMPLETED: '작업완료',
  ISSUE: '이슈',
}

export const WORK_PROGRESS_TEXT = {
  stepTitles: ['작업요청', '작업보고'] as const,
  none: '-',
  loading: '불러오는 중이에요',
  loadError: '불러오지 못했어요',
  approved: '검수완료',
} as const

export function reportStatusLabel(code: string | null | undefined): string | null {
  if (!code) return null
  return (REPORT_STATUS_LABEL as Record<string, string | undefined>)[code] ?? null
}

/** 마지막 보고일의 보고가 전부 검수됐으면 검수완료(이슈 보고는 검수 대상이 아니라 조치 대상이므로 이슈가 있는 날은 검수완료로 보지 않음) */
export function isReportApproved(days: ProgressReportDay[]): boolean {
  if (days.length === 0) return false
  const latest = days[days.length - 1]
  return latest.approvalYn === 'Y'
}

export type WorkProgressQuery =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; summary: TaskProgressSummary }

/**
 * 작업요청·작업보고 2단계. workdays = 공종 작업일 수(일요일·공휴일 제외, 휴일 정보를 못 쓰면 null).
 * ④ 발송 1건 이상 = 완료 / ⑤ 보고 있음 = 현재, 검수완료 = 완료. 둘 다 없으면 대기.
 */
export function getWorkProgressSteps(q: WorkProgressQuery, workdays: number | null): ConfirmProgressStep[] {
  const [t4, t5] = WORK_PROGRESS_TEXT.stepTitles
  if (q.status === 'loading') {
    return [
      { title: t4, text: WORK_PROGRESS_TEXT.loading, state: 'pending' },
      { title: t5, text: WORK_PROGRESS_TEXT.loading, state: 'pending' },
    ]
  }
  if (q.status === 'error') {
    return [
      { title: t4, text: WORK_PROGRESS_TEXT.loadError, state: 'pending' },
      { title: t5, text: WORK_PROGRESS_TEXT.loadError, state: 'pending' },
    ]
  }
  const s = q.summary

  // ④ 작업요청
  const sent = formatMonthDayTime(s.lastWorkOrderSentDtm)
  const orderDone = s.workOrderCount > 0
  const text4 = orderDone
    ? `${sent ? `${sent} 발송 · ` : ''}작업요청 ${s.workOrderCount}건`
    : WORK_PROGRESS_TEXT.none

  // ⑤ 작업보고
  const hasReport = s.reportDayCount > 0
  const approved = hasReport && isReportApproved(s.reportDays)
  let text5: string
  if (!hasReport) {
    text5 = WORK_PROGRESS_TEXT.none
  } else {
    const label = approved ? WORK_PROGRESS_TEXT.approved : reportStatusLabel(s.latestReportStatus)
    const count = workdays != null ? `보고 ${s.reportDayCount}/${workdays}일` : `보고 ${s.reportDayCount}일`
    text5 = [label, count].filter(Boolean).join(' · ')
  }

  return [
    {
      title: t4, text: text4, state: orderDone ? 'done' : 'pending',
      ...(orderDone && { detailKey: WORK_ORDER_DETAIL_KEY }),
    },
    {
      title: t5, text: text5, state: !hasReport ? 'pending' : approved ? 'done' : 'current',
      ...(hasReport && { detailKey: WORK_REPORT_DETAIL_KEY }),
    },
  ]
}

/**
 * 일정확인 3단계 뒤에 ④⑤를 이어 붙인다. 뒤 단계가 진행됐으면(작업요청 발송·보고 있음) 3단계 '일정 확정'은 지나간 단계로 본다.
 * (현재 단계가 둘이 되지 않게 3단계의 current 를 done 으로 내림)
 */
export function appendWorkProgressSteps(confirmSteps: ConfirmProgressStep[], workSteps: ConfirmProgressStep[]): ConfirmProgressStep[] {
  const later = workSteps.some(s => s.state === 'current' || s.state === 'done')
  const head = later
    ? confirmSteps.map((s, i) => (i === 2 && s.state === 'current' ? { ...s, state: 'done' as const } : s))
    : confirmSteps
  return [...head, ...workSteps]
}

/** 날짜 탭 라벨 'M/D' */
export function reportDayTabLabel(workDate: string): string {
  return formatMonthDay(workDate)
}
